import {
  AccessTime as AccessTimeIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Group as GroupIcon,
  Image as ImageIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Category } from "fileParsing/types";
import { extractRandomSample } from "fileParsing/wrangler/extractTemplates";
import { suggestDate } from "fileParsing/wrangler/transform";
import {
  DataPointTemplate,
  DataTemplate,
  DateParserArguments,
  Path,
  SupposedUsage,
  Visibility,
} from "fileParsing/wrangler/types";
import { useAppSelector } from "index";
import React, { ReactNode, useMemo, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { v4 } from "uuid";
import Node from "../Sidebar/Node";
import { CHIP_TYPE, Chip, ChipData, ChipProps } from "./Chip";

type Props = {
  template: DataPointTemplate;
  data: unknown[];
  onChange: (data: Partial<DataPointTemplate>) => void;
  onFormatTimestamp?: (
    timestamp: DataTemplate & {
      template: Path;
      arguments?: DateParserArguments;
    },
  ) => void;
  hovered?: string;
  setHovered: (path?: Path) => void;
  previewUri: (path?: Path) => void;
};

const DragDataChip: React.FC<ChipProps & { data: DataTemplate | object }> = ({ data, ...props }) => {
  const t = data as DataTemplate;
  const [, dragRef] = useDrag<ChipData>(
    () => ({
      type: CHIP_TYPE,
      item: {
        path: t.value as Path,
        label: "label" in t ? t.label : t.value,
        usage: props.supposedUsage,
        payload: data,
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      previewOptions: {
        captureDraggingState: false,
      },
    }),
    [data],
  );

  return <Chip ref={dragRef} showIcon={false} {...props} />;
};

const SortDrop: React.FC<{
  id: string;
  onDrop: (payload: object) => void;
  children: ReactNode;
}> = ({ id, onDrop, children }) => {
  const ref = useRef<HTMLDivElement>();

  const [, dropRef] = useDrop<ChipData, unknown, unknown>(() => ({
    accept: CHIP_TYPE,
    canDrop: ({ payload }) => {
      if (payload !== undefined && payload !== null && "id" in payload) {
        return payload.id !== id;
      }
      return false;
    },
    drop: ({ payload }, monitor) => {
      if (payload !== undefined && monitor.canDrop()) onDrop(payload);
    },
  }));

  dropRef(ref);
  return (
    <Box ref={ref} display="inline-block">
      {children}
    </Box>
  );
};

const TextValue: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
}> = ({ value, onChange }) => {
  const textValue = useRef<string>(value ?? "");
  const prevValue = useRef<string>(value ?? "");

  const debounce = useRef<NodeJS.Timeout>();
  const lastUpdate = useRef<number>(0);

  if (value && value !== prevValue.current) {
    textValue.current = value;
    prevValue.current = value;
  }

  return (
    <span
      style={{ display: "inline-block", outline: "none" }}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onInput={(ev) => {
        const text = ev.currentTarget.innerText;
        prevValue.current = text;

        if (debounce.current === undefined || Math.abs(Date.now() - lastUpdate.current) > 1500) {
          onChange?.(text);
          lastUpdate.current = Date.now();
        }
        clearTimeout(debounce.current);
        debounce.current = setTimeout(() => {
          onChange?.(text);
          lastUpdate.current = Date.now();
          debounce.current = undefined;
        }, 800);
      }}
    >
      {textValue.current}
    </span>
  );
};
type TagInputProps = {
  value?: DataTemplate[];
  onChange?: (value: DataTemplate[]) => void;
  hovered?: string;
  onHover: (path?: Path) => void;
};

const TagInput: React.FC<TagInputProps> = ({ value, onChange, hovered, onHover: onHovered }) => {
  return (
    <>
      {value?.map((el, ix) => {
        if (el.type === "data") {
          return (
            <DragDataChip
              key={el.id}
              selected={true}
              label={el.label}
              data={el}
              onDelete={() => {
                const combined = {
                  ...value[ix - 1],
                  value: (value[ix - 1].value as string) + (value[ix + 1].value as string),
                } as DataTemplate;
                onChange?.([...value.slice(0, ix - 1), combined, ...value.slice(ix + 2)]);
              }}
              hovered={Boolean(hovered && hovered.toString() === el.value.toString())}
              onHover={(b) => onHovered(b ? el.value : undefined)}
            />
          );
        } else if (el.type === "text") {
          return (
            <TextValue
              key={el.id}
              value={el.value}
              onChange={(v) => onChange?.(Object.assign([], value, { [ix]: { ...value[ix], value: v } }))}
            />
          );
        }
        return null;
      })}
    </>
  );
};

interface SimpleDropValueProps<T extends DataTemplate> {
  label: string;
  icon: "time" | "uri" | "contacts" | "location";
  values?: T[];
  supportedUsage?: SupposedUsage;
  onChange: (newArray: T[]) => void;
  onDrop: (value: DataTemplate) => T | undefined;
  onClick?: (value: T) => void;
  hovered?: string;
  onHover: (path?: Path) => void;
}

const SimpleDropValue: <T extends DataTemplate>(props: SimpleDropValueProps<T>) => React.ReactElement = ({
  label,
  values = [],
  supportedUsage,
  icon,
  onChange,
  onDrop,
  onClick,
  hovered,
  onHover,
}) => {
  const [{ canDrop, isOver }, dropRef] = useDrop<ChipData, unknown, { canDrop: boolean; isOver: boolean }>(
    () => ({
      accept: CHIP_TYPE,
      canDrop: ({ usage, payload }) => {
        if (payload !== undefined) {
          return false;
        }

        if (supportedUsage === undefined) {
          return true;
        } else {
          return usage?.format === supportedUsage?.format;
        }
      },
      drop: ({ path, label }) => {
        const dropVal = onDrop({
          id: v4(),
          type: "data",
          value: path,
          label,
        });
        if (dropVal) onChange([...values, dropVal]);
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.canDrop() && monitor.isOver({ shallow: true }),
      }),
    }),
    [values, onChange, onDrop],
  );

  const iconEl = useMemo(() => {
    switch (icon) {
      case "time":
        return <AccessTimeIcon fontSize="small" sx={{ transform: "translateY(2px)", marginRight: 1 }} />;
      case "uri":
        return <ImageIcon fontSize="small" sx={{ transform: "translateY(2px)", marginRight: 1 }} />;
      case "contacts":
        return <GroupIcon fontSize="small" sx={{ transform: "translateY(2px)", marginRight: 1 }} />;
      case "location":
        return <LocationOnIcon fontSize="small" sx={{ transform: "translateY(2px)", marginRight: 1 }} />;
    }
    return undefined;
  }, [icon]);

  return (
    <Stack
      direction="row"
      sx={{
        position: "relative",
        border: "1px solid #fff",
        borderRadius: 1,
        marginTop: "3px",
      }}
    >
      <Box padding={1}>
        <Stack direction="row">
          {iconEl}
          <Typography>{label}</Typography>
        </Stack>
      </Box>
      <Box position="relative" width="100%" borderLeft="1px solid #fff" padding={1} overflow="hidden">
        {values.length === 0
          ? " - drop here - "
          : values.map((el, ix) => (
              <SortDrop
                key={el.id}
                id={el.id}
                onDrop={(payload: object) => {
                  const el = payload as unknown as (typeof values)[0];
                  onChange([
                    ...values.slice(0, ix).filter(({ id }) => id !== el.id),
                    el,
                    ...values.slice(ix).filter(({ id }) => id !== el.id),
                  ]);
                }}
              >
                <DragDataChip
                  selected={true}
                  label={el.type === "data" ? el.label : el.value}
                  supposedUsage={supportedUsage}
                  data={el}
                  onClick={() => onClick?.(el)}
                  onDelete={() => {
                    onChange(values.filter(({ id }) => id !== el.id));
                  }}
                  hovered={Boolean(hovered && hovered.toString() === el.value.toString())}
                  onHover={(b) => onHover(b ? (el.value as Path) : undefined)}
                />
              </SortDrop>
            ))}
        <Box
          sx={(theme) => ({
            visibility: canDrop ? "visible" : "hidden",
            backgroundColor: theme.palette.primary.main,
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            borderRadius: isOver ? 2 : 0,
            margin: isOver ? 0.3 : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          Drop here
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          pointerEvents: canDrop ? "all" : "none",
        }}
        ref={dropRef}
      ></Box>
    </Stack>
  );
};

type TextAreaProps = {
  value: DataTemplate[];
  onChange: (value: DataTemplate[]) => void;
  hovered?: string;
  onHover: (path?: Path) => void;
};

const TextArea: React.FC<TextAreaProps> = ({ value, onChange, hovered, onHover }) => {
  const [{ canDrop, isOver }, dropRef] = useDrop<ChipData, unknown, { canDrop: boolean; isOver: boolean }>(
    () => ({
      accept: CHIP_TYPE,
      drop: ({ path, label }) => {
        onChange([...value, { id: v4(), type: "data", value: path, label }, { id: v4(), type: "text", value: "" }]);
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.canDrop() && monitor.isOver(),
      }),
    }),
    [value, onChange],
  );
  return (
    <Stack direction="row" border="1px solid #fff" borderRadius={1} marginTop="3px" ref={dropRef}>
      <Typography padding={1}>Text:</Typography>
      <Box
        sx={{
          position: "relative",
          borderLeft: "1px solid #fff",
          overflow: "scroll",
          width: "100%",
        }}
      >
        <TagInput value={value} onChange={onChange} hovered={hovered} onHover={onHover} />

        <Box
          sx={(theme) => ({
            visibility: canDrop ? "visible" : "hidden",
            backgroundColor: theme.palette.primary.main,
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            borderRadius: isOver ? 2 : 0,
            margin: isOver ? 0.5 : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          Drop here
        </Box>
      </Box>
    </Stack>
  );
};

type CategorySelectorProps = {
  category: Category | undefined;
  onChange: (value: Partial<DataPointTemplate>) => void;
};

const CategorySelector: React.FC<CategorySelectorProps> = ({ category, onChange }) => {
  const theme = useTheme();
  const categories = useAppSelector((state) => state.filters.categories);

  return (
    <Stack direction="row" alignItems="center" border="1px solid #fff" borderRadius={1}>
      <Typography padding={1}>Category</Typography>
      <FormControl sx={{ "& fieldset": { borderWidth: "0 !important" }, width: "100%" }}>
        <Select
          displayEmpty
          sx={{ fontWeight: "bold" }}
          value={category}
          onChange={(e) => onChange({ category: e.target.value as Category })}
          renderValue={(value) => {
            if (category === undefined) {
              return (
                <Typography fontWeight="bold" textAlign="center">
                  Select a Category
                </Typography>
              );
            }

            return (
              <>
                <Stack direction="row" justifyContent="center">
                  <Node color={theme.palette.categories[value].main} active={true} size={20} margin={0} />
                  <Typography fontWeight="bold" ml="10px">
                    {value}
                  </Typography>
                </Stack>
              </>
            );
          }}
        >
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <div style={{ float: "left", marginRight: 10 }}>
                <Node color={theme.palette.categories[c.key as Category].main} active={true} size={20} margin={0} />
              </div>
              <div style={{ float: "left" }}>{c.label}</div>
              <br style={{ clear: "both" }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

const SubCategory: React.FC<TextAreaProps> = ({ value, onChange, hovered, onHover }) => {
  const [{ canDrop, isOver }, dropRef] = useDrop<ChipData, unknown, { canDrop: boolean; isOver: boolean }>(
    () => ({
      accept: CHIP_TYPE,
      drop: ({ path, label }) => {
        onChange([...value, { id: v4(), type: "data", value: path, label }, { id: v4(), type: "text", value: "" }]);
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.canDrop() && monitor.isOver(),
      }),
    }),
    [value, onChange],
  );
  return (
    <Stack direction="row" border="1px solid #fff" borderRadius={1} marginTop="3px" ref={dropRef}>
      <Typography padding={1}>Title</Typography>
      <Box
        sx={{
          position: "relative",
          padding: 1,
          borderLeft: "1px solid #fff",
          overflow: "scroll",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        <TagInput value={value} onChange={onChange} hovered={hovered} onHover={onHover} />

        <Box
          sx={(theme) => ({
            visibility: canDrop ? "visible" : "hidden",
            backgroundColor: theme.palette.primary.main,
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            borderRadius: isOver ? 2 : 0,
            margin: isOver ? 0.5 : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          Drop here
        </Box>
      </Box>
    </Stack>
  );
};

const DataCard: React.FC<Props> = ({
  template,
  data,
  onChange,
  onFormatTimestamp,
  hovered,
  setHovered,
  previewUri,
}) => {
  const localPath = (path: Path) =>
    path.length > template.path.length && template.path.every((p, ix) => p === path[ix]);
  const strictLocalPath = (path: Path) =>
    (localPath(path) && path.slice(template.path.length + 1).every((p) => p !== null)) ||
    path.slice(template.path.length + 2).every((p) => p !== null);
  const determineTimeFormat: (path: Path) => DateParserArguments | undefined = (path) => {
    for (const tree of data) {
      const v = extractRandomSample(tree, path);
      if (typeof v === "number" || typeof v === "string") {
        const parsed = suggestDate(v);
        if (parsed) {
          return parsed.argument;
        }
      }
    }

    return undefined;
  };
  return (
    <Paper
      sx={{
        boxSizing: "border-box",
        margin: "30px 0",
        overflow: "hidden",
      }}
    >
      {/* TITLE */}
      <Stack
        direction="row"
        sx={(theme) => ({
          pr: "20px",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: theme.palette.background.tooltip,
          wordBreak: "break-all",
        })}
      >
        {/* TODO: Status-Icon: Complete/Incomplete */}
        {template.updated ? (
          <CircularProgress size="1.5rem" sx={{ mx: 1 }} />
        ) : (
          // <EditIcon fontSize="small" sx={{ mx: 1 }} />
          <CheckIcon fontSize="small" sx={{ mx: 1 }} />
        )}
        <Typography variant="caption">
          Template applied for{" "}
          <Typography variant="inherit" component="span" color="GrayText">
            {template.path.map((p) => (p === null ? "[]" : p)).join(".")}
          </Typography>{" "}
          with {template.dataPointsCount} Items
        </Typography>
        <IconButton onClick={() => onChange({ ...template, visibility: Visibility.hidden })}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Stack>
      {/* END-TITLE */}

      <Box sx={{ padding: "8px 16px 8px 16px" }}>
        <CategorySelector category={template.category} onChange={onChange} />

        <SubCategory
          value={template.subcategory}
          onChange={(subcategory) => onChange({ subcategory })}
          hovered={hovered}
          onHover={setHovered}
        />
        <SimpleDropValue
          label="Timestamp"
          icon="time"
          values={(template.selectedTimestamps ?? []).map((t) => ({
            id: v4(),
            type: "data" as const,
            label: `${t.path.slice(-1)[0]}`,
            value: t.path,
            arguments: t.arguments,
            template: template.path,
          }))}
          onChange={(selectedTimestamps) =>
            onChange({
              selectedTimestamps: selectedTimestamps.map((t) => ({
                path: t.value as Path,
                arguments: t.arguments,
                template: t.template,
              })),
            })
          }
          onDrop={({ id, ...t }) => {
            const path = t.value as Path;
            if (!strictLocalPath(path)) return undefined;
            const args = determineTimeFormat(path);
            if (args) {
              return {
                id,
                type: "data",
                value: path,
                label: t.type === "data" ? t.label : "- ERROR -",
                arguments: args,
                template: template.path,
              };
            }
            onFormatTimestamp?.({
              id,
              type: "data",
              value: path,
              label: t.type === "data" ? t.label : "- ERROR -",
              template: template.path,
            });
            return undefined;
          }}
          onClick={onFormatTimestamp}
          hovered={hovered}
          onHover={setHovered}
        />
        <SimpleDropValue
          label="URI"
          icon="uri"
          values={(template.selectedUris ?? []).map((t) => ({
            id: v4(),
            type: "data" as const,
            label: `${t.slice(-1)[0]}`,
            value: t,
            template: template.path,
          }))}
          supportedUsage={{ format: "uri" }}
          onChange={(selectedUris) => onChange({ selectedUris: selectedUris.map((t) => t.value as Path) })}
          onClick={(clickedUri) => previewUri(clickedUri.value)}
          onDrop={({ id, ...t }) => {
            const path = t.value as Path;
            if (!localPath(path)) return undefined;
            return {
              id,
              type: "data",
              value: path,
              label: t.type === "data" ? t.label : "- ERROR -",
              template: template.path,
            };
          }}
          hovered={hovered}
          onHover={setHovered}
        />
        <TextArea
          value={template.content}
          onChange={(content) => onChange({ content })}
          hovered={hovered}
          onHover={setHovered}
        />

        {/* <Button onClick={() => setShowMore((b) => !b)}>
          {showMore ? "Show Less" : "Show More"}
        </Button>
        <Collapse in={showMore}> */}
        <Divider />

        <SimpleDropValue
          label="Contacts"
          icon="contacts"
          values={template.selectedContacts.map((path) => ({
            id: v4(),
            type: "data" as const,
            value: path,
            label: String(path[path.length - 1]),
            template: template.path,
          }))}
          onChange={(selectedContacts) =>
            onChange({
              selectedContacts: selectedContacts.map((t) => t.value as Path),
            })
          }
          onDrop={({ id, ...t }) => ({
            id,
            type: "data",
            value: t.value as Path,
            label: t.type === "data" ? t.label : "- ERROR -",
            template: template.path,
          })}
          hovered={hovered}
          onHover={setHovered}
        />

        <Box
          sx={{
            "& > *": {
              marginTop: "8px",
              marginBottom: "8px",
            },
          }}
        >
          <SimpleDropValue
            label="Latitude"
            icon="location"
            values={template.selectedLatitudes.map((path) => ({
              id: v4(),
              type: "data" as const,
              value: path,
              label: String(path[path.length - 1]),
              template: template.path,
            }))}
            onChange={(selectedLatitudes) =>
              onChange({
                selectedLatitudes: selectedLatitudes.map((t) => t.value as Path),
              })
            }
            onDrop={({ id, ...t }) => {
              const path = t.value as Path;
              if (!localPath(path)) return undefined;
              return {
                id,
                type: "data",
                value: path,
                label: t.type === "data" ? t.label : "- ERROR -",
                template: template.path,
              };
            }}
            hovered={hovered}
            onHover={setHovered}
          />
          <SimpleDropValue
            label="Longitude"
            icon="location"
            values={template.selectedLongitudes.map((path) => ({
              id: v4(),
              type: "data" as const,
              value: path,
              label: String(path[path.length - 1]),
              template: template.path,
            }))}
            onChange={(selectedLongitudes) =>
              onChange({
                selectedLongitudes: selectedLongitudes.map((t) => t.value as Path),
              })
            }
            onDrop={({ id, ...t }) => {
              const path = t.value as Path;
              if (!localPath(path)) return undefined;
              return {
                id,
                type: "data",
                value: path,
                label: t.type === "data" ? t.label : "- ERROR -",
                template: template.path,
              };
            }}
            hovered={hovered}
            onHover={setHovered}
          />
        </Box>
        {/* </Collapse> */}
      </Box>
    </Paper>
  );
};

export default DataCard;
